import { watch, type Ref } from "vue";
import type { VeeForm } from "@/types/vee-validate";

/**
 * Compose un {@code watch} qui efface l'erreur server-side d'un champ
 * vee-validate des que sa valeur change ( retype utilisateur ) .
 *
 * <p><b>Pourquoi</b> : apres un submit en echec ( ex backend retourne
 * 422 BAD_CURRENT_PASSWORD ) , le code appelle
 * {@code observer.setFieldError(fieldName , message)} pour marquer le
 * champ en rouge sous l'input . Mais sans hook reactif , le rouge
 * reste affiche meme apres correction par l'utilisateur , tant qu'un
 * nouveau submit n'a pas round-trip le backend . Cette persistance
 * est tres perturbante : "le mot de passe saisi est incorrect" reste
 * affiche alors que le mdp est OK et c'est un autre champ qui a
 * casse le commit ( ex EXISTING_EMAIL 422 ) .
 *
 * <p><b>Usage</b> : a appeler dans le {@code setup()} apres avoir
 * declare le ref de la valeur + le ref observer du Form vee-validate .
 *
 * <pre>{@code
 * const password = useText().refText;
 * const observer = ref<VeeForm | null>(null);
 * useFieldErrorReset(password, observer, "change_password.password");
 * }</pre>
 *
 * <p><b>Contrat</b> : le clear est appele a CHAQUE changement de
 * valeur , meme si aucune erreur server-side n'etait active . C'est
 * volontaire et benin ( setFieldError sur un champ deja sans erreur
 * est un no-op ) ; cela evite de devoir traquer un flag "isErrored" .
 *
 * @param field      ref reactif sur la valeur du champ ( ex le {@code .value}
 *                   d'un {@code useText().refText} )
 * @param observer   ref vers la balise {@code <Form ref="observer">}
 * @param fieldName  nom du champ tel que declare dans l'attribut {@code name}
 *                   du {@code <TextField>} ( ex "change_password.password" )
 */
export function useFieldErrorReset(
  field: Ref<string>,
  observer: Ref<VeeForm | null>,
  fieldName: string,
): void {
  watch(field, () => {
    observer.value?.setFieldError(fieldName, "");
  });
}
